import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslate } from "helpers";
import { TireSetDetailProps } from "./types";
import { Grid } from "ui/components/Grid";
import { AvailableWidget, GridRef } from "ui/components/Grid/types";
import { generateGridFromTemplate } from "ui/components/Grid/helper";
import { GridTemplate, GRID_TEMPLATES } from "ui/components/Grid/templates";
import { TIRE_SET_AVAILABLE_WIDGETS } from "./consts";
import WidgetEditCard from "ui/modules/contacts/pages/ContactDetailPage/components/ContactDetailCard/components/WidgetEditCard/WidgetEditCard";

// v2: dodan widget povijesti korištenja; stari spremljeni raspored ga ne bi prikazao.
const STORAGE_KEY = "tire-set-detail-state-v2";

const TireSetDetailCard: React.FC<TireSetDetailProps> = (props) => {
  const { tireSet, showAddWidgetModal, setShowAddWidgetModal } = props;
  const { t } = useTranslate();

  const gridRef = useRef<GridRef>(null);

  const [savedState] = useState(() =>
    JSON.parse(localStorage.getItem(STORAGE_KEY) || "null")
  );
  const lastLayoutsRef = useRef<any>(savedState?.layouts ?? null);

  const allWidgets = TIRE_SET_AVAILABLE_WIDGETS(t, tireSet);

  const initialUserWidgets = savedState?.userWidgets ?? allWidgets.map((w) => w.id);

  const [userWidgets, setUserWidgets] = useState<string[]>(initialUserWidgets);
  const [template, setTemplate] = useState<GridTemplate>(
    savedState?.template ?? "two-columns-left"
  );

  const templateConfig = useMemo(
    () => GRID_TEMPLATES.find((tpl) => tpl.id === template) || GRID_TEMPLATES[0],
    [template]
  );

  const { widgets, layouts } = useMemo(() => {
    const availableWidgets = userWidgets
      .map((id) => allWidgets.find((w) => w.id === id))
      .filter(Boolean) as AvailableWidget[];

    return generateGridFromTemplate(templateConfig, availableWidgets);
  }, [userWidgets, t, tireSet, templateConfig]);

  const saveState = useCallback(
    (layoutsToSave?: any) => {
      if (layoutsToSave) lastLayoutsRef.current = layoutsToSave;
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          userWidgets,
          template,
          layouts: layoutsToSave ?? lastLayoutsRef.current ?? layouts,
        })
      );
    },
    [userWidgets, template, layouts]
  );

  useEffect(() => {
    if (tireSet.id) saveState();
  }, [userWidgets, template, saveState, tireSet.id]);

  const handleTemplateChange = useCallback(
    (newTemplate: GridTemplate) => {
      setTemplate(newTemplate);
      lastLayoutsRef.current = null;
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          userWidgets,
          template: newTemplate,
          layouts: null,
        })
      );
    },
    [userWidgets]
  );

  const handleEditWidgetSuccess = useCallback(() => {
    setShowAddWidgetModal(false);
  }, []);

  return (
    <>
      <Grid
        key={`${userWidgets.join(",")}-${template}-${tireSet.id}`}
        ref={gridRef}
        initialWidgets={widgets}
        initialLayouts={lastLayoutsRef.current ?? layouts}
        onLayoutChange={(newLayouts) => saveState(newLayouts)}
      />

      <WidgetEditCard
        show={showAddWidgetModal}
        availableWidgets={allWidgets}
        userWidgets={userWidgets}
        setUserWidgets={setUserWidgets}
        template={template}
        onTemplateChange={handleTemplateChange}
        onSuccess={handleEditWidgetSuccess}
        onClose={() => setShowAddWidgetModal(false)}
      />
    </>
  );
};
export default memo(TireSetDetailCard);
